// The lightbox's zoom: a click zooms in a step about the point clicked, and a
// click at the deepest step goes back to fitting the image. Positions are
// measured from the centre of the stage, and `fitted` is the image's size at
// scale 1. Swift twin: `LightboxZoom` in ImageLightbox.swift.

export interface Size { width: number; height: number }
export interface Point { x: number; y: number }
export interface LightboxZoom { scale: number; x: number; y: number }

export const ZOOM_LEVELS = [1, 2, 4] as const;
const DEEPEST = 4;
export const FITTED: LightboxZoom = { scale: 1, x: 0, y: 0 };

export function isZoomed(zoom: LightboxZoom): boolean {
  return zoom.scale > 1;
}

export function canZoomIn(zoom: LightboxZoom): boolean {
  return zoom.scale < DEEPEST;
}

/** Whether `point` lands on the image as it is drawn now. */
export function zoomContains(zoom: LightboxZoom, point: Point, fitted: Size): boolean {
  return Math.abs(point.x - zoom.x) <= (fitted.width * zoom.scale) / 2
    && Math.abs(point.y - zoom.y) <= (fitted.height * zoom.scale) / 2;
}

/** The zoom after a click at `point`: the next step in, keeping the pixel
 * under the pointer where it is, or back to fitting from the last step. */
export function zoomClicked(zoom: LightboxZoom, point: Point, fitted: Size, stage: Size): LightboxZoom {
  const next = ZOOM_LEVELS.find((level) => level > zoom.scale);
  if (next === undefined) return FITTED;
  // The image point under the pointer, in fitted units from its centre.
  const imageX = (point.x - zoom.x) / zoom.scale;
  const imageY = (point.y - zoom.y) / zoom.scale;
  return zoomClamped({ scale: next, x: point.x - imageX * next, y: point.y - imageY * next }, fitted, stage);
}

/** The offset limited so the zoomed image always covers the stage on an axis
 * where it is larger than it, and stays centred on one where it is not. */
export function zoomClamped(zoom: LightboxZoom, fitted: Size, stage: Size): LightboxZoom {
  const limitX = Math.max(0, (fitted.width * zoom.scale - stage.width) / 2);
  const limitY = Math.max(0, (fitted.height * zoom.scale - stage.height) / 2);
  return {
    scale: zoom.scale,
    x: Math.min(Math.max(zoom.x, -limitX), limitX) || 0,
    y: Math.min(Math.max(zoom.y, -limitY), limitY) || 0,
  };
}

/** The neighbouring index, stopping at either end rather than wrapping. */
export function steppedIndex(index: number, delta: number, count: number): number {
  return Math.min(Math.max(index + delta, 0), Math.max(count - 1, 0));
}
